import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Run a side effect exactly once, AFTER the commit that applied a setData updater.
 *
 * Usage: call stage(fn) INSIDE the updater (a ref write is safe there: StrictMode's
 * double invocation just overwrites the same slot, and the last rebased invocation
 * wins, so the staged closure always matches the committed state), then schedule()
 * right after the setData call to arm the flush.
 *
 * This replaces the old setTimeout-inside-the-updater pattern: StrictMode fired those
 * timers twice per update, and a discarded concurrent render's timer persisted state
 * that never committed. An unmount before the flush still runs the staged effect
 * (cleanup flush), so a save scheduled right before navigation is not lost.
 */
export function useDeferredCommit() {
  const pendingRef = useRef<(() => void) | null>(null);
  const [tick, setTick] = useState(0);

  const flush = () => {
    const run = pendingRef.current;
    if (!run) return;
    pendingRef.current = null;
    run();
  };

  useEffect(() => {
    flush();
  }, [tick]);

  // Unmount flush: a staged effect must not evaporate when the screen is left
  // before React processes the armed tick.
  useEffect(() => () => flush(), []);

  const stage = useCallback((fn: () => void) => {
    pendingRef.current = fn;
  }, []);

  const schedule = useCallback(() => {
    setTick((t) => t + 1);
  }, []);

  return { stage, schedule };
}
